import { useEffect, useMemo, useState } from 'react';
import { App } from 'antd';
import { useAuthStore } from '@/store/authStore';
import { matchesSelectedBu } from '@/utils/businessUnits';
import { vendorApi } from '@/utils/vendorApi';
import { VendorItem, VendorListResponse } from '@/types';

/** Empty selection. Omitted from list requests. */
export const ALL_VENDORS = '';

const OPTION_PAGE_LIMIT = 100;
const OPTION_MAX_PAGES = 10;

export type VendorOptionSource = 'admin' | 'operator';

export interface VendorIdOption {
  value: string;
  label: string;
}

async function loadVendorOptionRows(
  source: VendorOptionSource,
  includeArchived: boolean,
): Promise<VendorItem[]> {
  const collected: VendorItem[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < OPTION_MAX_PAGES; page += 1) {
    const params = {
      includeArchived: includeArchived || undefined,
      limit: OPTION_PAGE_LIMIT,
      nextToken: cursor,
    };
    const response: VendorListResponse = source === 'admin'
      ? await vendorApi.listVendors(params)
      : await vendorApi.listOperatorVendorsPage(params);
    collected.push(...response.vendors);
    if (!response.nextToken) {
      return collected;
    }
    cursor = response.nextToken;
  }
  console.warn(
    `Vendor ID options: pagination cap reached (${OPTION_MAX_PAGES} pages); remaining vendors omitted`
  );
  return collected;
}

/**
 * Vendor ID dropdown options for the caller's business-unit scope and the
 * header selection. `source` chooses the admin or operator list API.
 * Archived vendors are included only when `includeArchived` is true.
 */
export function useVendorIdOptions(
  source: VendorOptionSource,
  includeArchived: boolean,
): {
  vendorIdOptions: VendorIdOption[];
  optionsLoading: boolean;
  optionRows: VendorItem[] | null;
} {
  const { message } = App.useApp();
  const { authorization, currentBusinessUnit } = useAuthStore();
  const [optionRows, setOptionRows] = useState<VendorItem[] | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadOptions = async () => {
      setOptionsLoading(true);
      try {
        const rows = await loadVendorOptionRows(source, includeArchived);
        if (!cancelled) {
          setOptionRows(rows);
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setOptionRows([]);
          const errorMessage = error instanceof Error ? error.message : 'Failed to load vendor IDs';
          message.error(errorMessage);
        }
      } finally {
        if (!cancelled) {
          setOptionsLoading(false);
        }
      }
    };
    void loadOptions();
    return () => {
      cancelled = true;
    };
  }, [source, includeArchived, message]);

  const selectedId = currentBusinessUnit?.id;
  const vendorIdOptions = useMemo(() => {
    const ids = new Set<string>();
    for (const row of optionRows ?? []) {
      if (!includeArchived && row.lifecycleStatus === 'archived') {
        continue;
      }
      if (selectedId && !matchesSelectedBu(row.bu, selectedId, authorization?.catalog)) {
        continue;
      }
      if (row.vendorId) {
        ids.add(row.vendorId);
      }
    }
    const sorted = [...ids].sort((left, right) => left.localeCompare(right));
    return [
      { value: ALL_VENDORS, label: 'All vendors' },
      ...sorted.map((id) => ({ value: id, label: id })),
    ];
  }, [optionRows, includeArchived, selectedId, authorization?.catalog]);

  return { vendorIdOptions, optionsLoading, optionRows };
}
